import { sqItemRuntimeStore } from '@/core/core.stores';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import type { ItemYAxisExtremes } from '@/trendData/itemRuntime.store';

/**
 * Returns the computed y-axis extremes for an item, falling back to its parent's entry; children
 * render on their parent's axis (they previously received cloned copies of these fields via
 * CHILD_CLONED_PROPERTIES).
 */
export function getComputedYAxisExtremes(item: { id: string; isChildOf?: string }): ItemYAxisExtremes | undefined {
  const { yAxisExtremes } = sqItemRuntimeStore.getState();
  return yAxisExtremes[item.id] ?? (item.isChildOf ? yAxisExtremes[item.isChildOf] : undefined);
}

/**
 * Overlays the computed y-axis extremes onto an item so readers see the effective values. Items
 * without a computed entry are returned as-is (identity preserved), leaving their persisted
 * fields, user-set extremes or values rehydrated from a workstep, in effect.
 */
export function withEffectiveYAxisExtremes<T extends { id: string; isChildOf?: string }>(item: T): T {
  const computed = getComputedYAxisExtremes(item);
  return computed ? { ...item, ...computed } : item;
}

/**
 * Records "a data fetch was just requested" for the item so plugins can recognize the fetch.
 */
export function stampLastFetchRequest(id: string): void {
  sqItemRuntimeStore.actions.setLastFetchRequest(id, new Date().toISOString());
}

/**
 * Records the request state carried by a data-status or results payload; a payload without
 * details clears the stored values.
 */
export function recordDataStatus(
  dataStatus: string,
  payload: {
    id: string;
    message?: unknown;
    warningCount?: unknown;
    warningLogs?: unknown;
    errorType?: unknown;
    errorCategory?: unknown;
    isSharedRequest?: unknown;
    timingInformation?: unknown;
    meterInformation?: unknown;
  },
): void {
  sqItemRuntimeStore.actions.setDataStatus(payload.id, dataStatus, {
    message: payload.message as string | undefined,
    warningCount: payload.warningCount as number | undefined,
    warningLogs: payload.warningLogs,
    errorType: payload.errorType as string | undefined,
    errorCategory: payload.errorCategory as string | undefined,
    isSharedRequest: payload.isSharedRequest as boolean | undefined,
    timingInformation: payload.timingInformation as string | undefined,
    meterInformation: payload.meterInformation as string | undefined,
  });
}

/**
 * Returns the item's data-request status; items that have not started fetching are INITIALIZING.
 */
export function getItemDataStatus(item: { id: string }): string {
  return sqItemRuntimeStore.getState().requestStatus[item.id]?.dataStatus ?? ITEM_DATA_STATUS.INITIALIZING;
}

/**
 * Overlays the item's request status and telemetry (progress, timing and data-read information)
 * onto it so readers see the runtime values. Items with no entries are returned as-is (identity
 * preserved).
 */
export function withRequestState<T extends { id: string }>(item: T): T {
  const { requestStatus, requestTelemetry } = sqItemRuntimeStore.getState();
  const status = requestStatus[item.id];
  const telemetry = requestTelemetry[item.id];
  if (!status && !telemetry) {
    return item;
  }
  return { ...item, ...status, ...telemetry };
}
